import { Link } from 'react-router-dom';

import type { Task } from '@/types/task';
import { formatRelativeDueDate, getTaskStatus } from '@/utils/task-display-utils';
import { CircleCheckbox } from '@/components/common/CircleCheckbox';
import { DASHBOARD_PREP_VISIBLE_TASKS } from '@/constants/dashboard-constants';

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

/** State A: no tasks yet */
function PrepCenterEmpty() {
  return (
    <div
      className="bg-[#252320] border-[0.5px] border-[#2d2a26] shadow-[0_10px_30px_rgba(0,0,0,0.5)] rounded-lg px-5 py-4"
      data-testid="prep-center-empty"
    >
      <p
        className="text-[10px] font-bold uppercase tracking-widest text-[#9a9590] mb-3"
        data-testid="prep-center-header"
      >
        PREP · READY TO BEGIN?
      </p>
      <p className="text-[12px] text-[#9a9590] mb-3">Generate a checklist to start prepping</p>
      <Link
        to="/templates"
        className="inline-block px-4 py-2.5 rounded bg-[#c8651a] hover:bg-[#e08a3d] active:bg-[#a84f10] transition-colors text-white text-[12px] font-bold uppercase tracking-wider"
        data-testid="prep-center-browse-templates"
      >
        Browse Templates
      </Link>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

interface PrepCenterProps {
  /** Prioritized upcoming tasks. PrepCenter shows only the first (priority) task. */
  tasks: Task[];
  /** Season ID for the "View All Tasks" link */
  seasonId?: string;
  /** Called when the user completes the priority task via CircleCheckbox */
  onQuickComplete?: (id: string) => Promise<void>;
  /** Error message keyed by task ID */
  taskErrors?: Record<string, string>;
}

export function PrepCenter({ tasks, seasonId, onQuickComplete, taskErrors }: PrepCenterProps) {
  if (tasks.length === 0) {
    return <PrepCenterEmpty />;
  }

  const priorityTask = tasks.find((t) => !t.blocked) ?? tasks[0];
  const today = new Date().toISOString().slice(0, 10);
  const status = getTaskStatus(priorityTask, today);
  const isOverdue = status === 'overdue';
  const relativeDate = formatRelativeDueDate(priorityTask.dueDate, today);
  const errorMessage = taskErrors?.[priorityTask.id];

  const viewAllHref = seasonId
    ? `/prep/tasks?season=${seasonId}`
    : '/seasons';

  const visibleCount = DASHBOARD_PREP_VISIBLE_TASKS;
  const totalCount = tasks.length;

  return (
    <div
      className="bg-[#252320] border-[0.5px] border-[#2d2a26] shadow-[0_10px_30px_rgba(0,0,0,0.5)] rounded-lg px-5 py-4"
      data-testid="prep-center"
      data-task-count-visible={visibleCount}
      data-task-count-total={totalCount}
    >
      {/* Header row */}
      <div className="flex items-center justify-between mb-3">
        <p
          className="text-[10px] font-bold uppercase tracking-widest text-[#9a9590]"
          data-testid="prep-center-header"
        >
          PREP
        </p>
        <Link
          to={viewAllHref}
          className="text-[11px] text-[#c8651a] hover:text-[#e08a3d] transition-colors"
          data-testid="prep-center-view-all"
        >
          View All Tasks →
        </Link>
      </div>

      {/* Priority task row: CircleCheckbox sibling to text (never nested) */}
      <div className="flex items-center gap-3" data-testid="prep-center-priority-row">
        {onQuickComplete && (
          <div className="shrink-0" data-testid="prep-center-checkbox-wrapper">
            <CircleCheckbox
              checked={priorityTask.completed}
              disabled={priorityTask.blocked}
              onChange={() => { void onQuickComplete(priorityTask.id); }}
              label={`Complete: ${priorityTask.title}`}
            />
          </div>
        )}

        <div className="flex-1 min-w-0">
          <p
            className={`text-[14px] font-medium leading-tight truncate ${
              isOverdue
                ? 'text-[#e08a3d]'
                : 'text-[#e8e5e0]'
            }`}
            data-testid="prep-center-task-title"
            data-state={isOverdue ? 'overdue' : 'upcoming'}
          >
            {priorityTask.title}
          </p>
          <p className="text-[11px] text-[#9a9590] mt-0.5" data-testid="prep-center-task-due">
            {relativeDate}
          </p>
          {errorMessage && (
            <p
              className="text-[11px] text-[#e08a3d] mt-0.5"
              data-testid="prep-center-task-error"
            >
              {errorMessage}
            </p>
          )}
        </div>
      </div>

      {/* Task count summary */}
      {tasks.length > visibleCount && (
        <p
          className="text-[10px] text-[#9a9590] mt-2.5"
          data-testid="prep-center-task-count"
        >
          +{tasks.length - visibleCount} more upcoming
        </p>
      )}
    </div>
  );
}
